import React, { FC } from 'react';
import type { SectionErrorKind } from './useLadderSection';

/**
 * One of the five impact sections on the Browser Ladder tab. Each panel owns
 * its own loading / error / retry state so one slow section never blanks the
 * others.
 */

export interface SectionPanelProps {
  index: number;
  title: string;
  /** The plain-English question this section answers. */
  question: string;
  loading: boolean;
  /** Server is computing fresh numbers for a cold cache; we are polling. */
  warming?: boolean;
  error: SectionErrorKind | null;
  onRetry: () => void;
  /** Rendered when the section has data. */
  children?: React.ReactNode;
}

const ERROR_COPY: Record<SectionErrorKind, string> = {
  timeout:
    'This section took too long to load. The rest of the page is unaffected.',
  failed: 'This section failed to load. The rest of the page is unaffected.',
};

export const SectionPanel: FC<SectionPanelProps> = ({
  index,
  title,
  question,
  loading,
  warming = false,
  error,
  onRetry,
  children,
}) => (
  <section className='ladder-section' aria-busy={loading}>
    <header className='ladder-section-header'>
      <h2>
        <span className='scaling-card-index'>{index}</span>
        {title}
      </h2>
      <p className='scaling-intro'>{question}</p>
    </header>
    {error && (
      <div className='ladder-section-error' role='alert'>
        <span>{ERROR_COPY[error]}</span>
        <button type='button' className='day-btn active' onClick={onRetry}>
          Retry
        </button>
      </div>
    )}
    {loading && !error && (
      <div className='ladder-section-loading'>
        {warming
          ? `Computing fresh numbers for ${title} — this can take up to a minute the first time after a deploy…`
          : `Loading ${title}…`}
      </div>
    )}
    {!error && children}
  </section>
);
